import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell, Legend,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, HeartPulse, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

// Colours drawn from the FDNY / severity palette. Bronx = deepest red (worst),
// Staten Island = green (best) — matches the true 6-month pattern.
const BOROUGH_COLORS = {
  "BRONX": "#dc2626",
  "MANHATTAN": "#f97316",
  "QUEENS": "#eab308",
  "BROOKLYN": "#84cc16",
  "RICHMOND / STATEN ISLAND": "#22c55e",
  "UNKNOWN": "#64748b",
};

const SEVERITY_LABELS = {
  "1": "1 · Life-Threat",
  "2": "2 · Serious",
  "3": "3 · Emergency",
  "4": "4 · Urgent",
  "5": "5 · Injury/Illness",
  "6": "6 · Minor",
  "7": "7 · Non-Emergency",
  "8": "8 · Transport",
  "9": "9 · Other",
};

const secondsToMinutes = (s) => Number(((s || 0) / 60).toFixed(2));

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-sm p-2 shadow-lg font-mono text-xs">
      <div className="font-semibold mb-1">{label}</div>
      <div>Avg response: <span className="text-primary">{row.pretty}</span></div>
      <div>Calls sampled: <span className="text-muted-foreground">{(row.count || 0).toLocaleString()}</span></div>
    </div>
  );
};

export default function EmsAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data: payload } = await api.get("/analytics/ems/response-times");
      setData(payload);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Failed to load analytics";
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => { setBusy(true); await load(); setBusy(false); };

  const boroughChart = (data?.by_borough || [])
    .filter((r) => r.borough && r.borough !== "UNKNOWN")
    .map((r) => ({
      borough: r.borough === "RICHMOND / STATEN ISLAND" ? "STATEN ISLAND" : r.borough,
      minutes: secondsToMinutes(r.avg_seconds),
      pretty: r.avg_seconds_pretty,
      count: r.count,
      fill: BOROUGH_COLORS[r.borough] || "#0ea5e9",
    }));

  const severityChart = (data?.by_severity || [])
    .filter((r) => r.severity && ["1","2","3","4","5","6","7","8","9"].includes(r.severity))
    .map((r) => ({
      severity: SEVERITY_LABELS[r.severity] || `Sev ${r.severity}`,
      minutes: secondsToMinutes(r.avg_seconds),
      pretty: r.avg_seconds_pretty,
      count: r.count,
      // Colour gradient: 1 (life-threat) red → 9 (other) green
      fill: ["#dc2626","#ef4444","#f97316","#eab308","#84cc16","#22c55e","#14b8a6","#0ea5e9","#64748b"][parseInt(r.severity, 10) - 1] || "#0ea5e9",
    }));

  return (
    <div className="absolute inset-0 overflow-y-auto" data-testid="ems-analytics-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-xs text-primary tracking-widest">// EMS ANALYTICS</div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-red-500" /> NYC EMS Response Times
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://data.cityofnewyork.us/Public-Safety/EMS-Incident-Dispatch-Data/76xm-jjuj"
              target="_blank" rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 font-mono"
              data-testid="ems-source-link"
            >
              <ExternalLink className="w-3 h-3" /> data.cityofnewyork.us
            </a>
            <Button size="sm" variant="outline" onClick={refresh} disabled={busy || loading} data-testid="ems-refresh-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Refresh
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Pulling from NYC Open Data…
          </div>
        )}

        {!loading && error && (
          <Card className="border-red-500/40">
            <CardHeader><CardTitle className="text-red-500">Failed to load</CardTitle></CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">{error}</div>
              <div className="text-xs text-muted-foreground mt-2">The NYC Open Data feed may be temporarily unavailable. Try again in a minute.</div>
            </CardContent>
          </Card>
        )}

        {!loading && !error && data && (
          <>
            <Card data-testid="ems-borough-card">
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Average End-to-End Response by Borough</CardTitle>
                <CardDescription>
                  Rolling {data.window_days}-day window · dataset last updated by NYC monthly · {(data.by_borough?.reduce((a,r) => a + (r.count||0), 0) || 0).toLocaleString()} calls sampled
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={boroughChart} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                      <CartesianGrid stroke="rgba(148,163,184,0.15)" strokeDasharray="3 3" />
                      <XAxis dataKey="borough" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: "minutes", angle: -90, position: "insideLeft", offset: 12, style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" } }} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
                      <Bar dataKey="minutes" radius={[4,4,0,0]}>
                        {boroughChart.map((row, i) => <Cell key={i} fill={row.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-xs text-muted-foreground mt-3 font-mono">
                  {boroughChart.map((r) => (
                    <div key={r.borough} className="inline-block mr-4">
                      <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ backgroundColor: r.fill }} />
                      {r.borough}: <span className="text-primary">{r.pretty}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="ems-severity-card">
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Average Response by Initial Severity Level</CardTitle>
                <CardDescription>Severity 1 = life-threat, severity 9 = other. Lower = faster response.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80 sm:h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={severityChart} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                      <CartesianGrid stroke="rgba(148,163,184,0.15)" strokeDasharray="3 3" />
                      <XAxis dataKey="severity" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={80} />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: "minutes", angle: -90, position: "insideLeft", offset: 12, style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" } }} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
                      <Bar dataKey="minutes" radius={[4,4,0,0]}>
                        {severityChart.map((row, i) => <Cell key={i} fill={row.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="text-xs text-muted-foreground font-mono">
              Source: {data.source} · window since {data.since?.slice(0, 10)} · cached server-side for 1 hour
            </div>
          </>
        )}
      </div>
    </div>
  );
}
